import React, { useEffect, useRef } from 'react';

import PropTypes from 'prop-types';
import ContentEditable from 'react-contenteditable';
import { useTranslation } from 'react-i18next';

import { useEditor, useNode } from '@craftjs/core';

import TextStyle from '@resolvers/tools/TextStyle/TextStyle';
import FontSizeStyle from '@resolvers/tools/FontSizeStyle/FontSizeStyle';
import PaddingStyle from '@resolvers/tools/PaddingStyle/PaddingStyle';
import SizeStyle from '@resolvers/tools/SizeStyle/SizeStyle';

import SettingsWrapper from '@common/components/SettingsWrapper/SettingsWrapper';
import SettingsRow from '@common/components/SettingsWrapper/SettingsRow';
import { EDITOR_RESOLVERS } from '@common/constants';

import AddTextPlaceholder from '@resolvers/tools/ParagraphComponents/AddTextPlaceholder';
import DisplayStyle from '@resolvers/tools/DisplayStyle/DisplayStyle';
import CreateLink from '@resolvers/tools/ParagraphComponents/CreateLink';
import {
  getNodeAtPath,
  placeCaretInstead,
  pathTo,
  placeCaretAfter,
  getBrByPath,
  getSelectionClosestDiv,
  formatHtmlForEmail,
} from '@common/helpers/domManipulationHelpers';

const PdfParagraph = ({
  text,
  fontSize,
  fontItalic,
  textAlign,
  background,
  paddingLeft,
  paddingRight,
  paddingTop,
  paddingBottom,
  fontWeight,
  textDecoration,
  fontColor,
  size,
  display,
  fontFamily,
}) => {
  const { actions } = useEditor();
  const {
    connectors: { connect, drag },
    actions: { setCustom, setProp },
    id,
  } = useNode();

  const ref = useRef(null);

  const { t } = useTranslation();

  useEffect(() => {
    if (ref?.current) {
      connect(drag(ref.current));
    }
  }, []);

  useEffect(() => {
    window.nodeRefs = window.nodeRefs || {};
    window.nodeRefs[id] = ref.current;

    return () => {
      delete window.nodeRefs[id];
    };
  }, [id]);

  useEffect(() => {
    actions.history.ignore(() => {
      setCustom((props) => {
        props.displayName = t('text');
      });
    });
  }, [setCustom, t]);

  const lastDivPathRef = React.useRef(null);

  const handleOnChange = (e) => {
    const root = ref.current;
    const selection = window.getSelection?.();

    lastDivPathRef.current = null;
    if (root && selection && selection.rangeCount) {
      const div = getSelectionClosestDiv(selection);
      if (div && root.contains(div)) {
        lastDivPathRef.current = pathTo(div, root);
      }
    }

    const newHtml = formatHtmlForEmail(e.target.value);

    setProp((props) => {
      props.text = newHtml;
    }, 500);

    requestAnimationFrame(() => {
      const rootP = ref.current;
      const divPath = lastDivPathRef.current;
      if (!rootP || !divPath) return;

      const nodeAtPath = getNodeAtPath({ root: rootP, path: divPath });
      const brNode = getBrByPath(nodeAtPath);

      if (brNode) {
        placeCaretAfter(brNode);
      } else {
        placeCaretInstead(rootP);
      }
    });
  };

  return (
    <ContentEditable
      innerRef={ref}
      html={text}
      // eslint-disable-next-line no-param-reassign
      onChange={handleOnChange}
      spellCheck={false}
      autoCorrect='off'
      autoCapitalize='off'
      tagName='p'
      onClickCapture={(e) => {
        const a = e.target.closest('a');
        if (a && ref.current?.contains(a)) e.preventDefault();
      }}
      onMouseDownCapture={(e) => {
        const a = e.target.closest('a');
        if (a && ref.current?.contains(a)) e.preventDefault();
      }}
      onKeyDownCapture={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && e.target.closest('a')) {
          e.preventDefault();
        }
      }}
      style={{
        fontSize: `${fontSize && fontSize > 0 ? fontSize : 10}px`,
        width: '100',
        textAlign: `${textAlign}`,
        fontWeight: `${fontWeight ? 'bold' : '100'}`,
        textDecoration: `${textDecoration ? 'underline' : 'none'}`,
        fontStyle: `${fontItalic ? 'italic' : 'normal'}`,
        margin: 0,
        color: `${fontColor}`,
        backgroundColor: `${background}`,
        fontFamily: fontFamily || 'sans-serif',
        whiteSpace: 'pre-wrap',
        paddingLeft: `${paddingLeft || 0}px`,
        paddingRight: `${paddingRight || 0}px`,
        paddingTop: `${paddingTop || 0}px`,
        paddingBottom: `${paddingBottom || 0}px`,
        width: `${size}%`,
        boxSizing: 'border-box',
        display: display,
      }}
    />
  );
};

const PdfParagraphConfigPanel = () => {
  return (
    <div>
      <SettingsWrapper>
        <SettingsRow sx={{ flexDirection: 'column', mb: 2 }}>
          <AddTextPlaceholder />
          <CreateLink />
        </SettingsRow>
        <SettingsRow sx={{ flexDirection: 'column', mb: 2 }}>
          <SizeStyle />
          <FontSizeStyle />
        </SettingsRow>
        <SettingsRow>
          <TextStyle isParagraph={true} />
        </SettingsRow>
        <PaddingStyle />
        <SettingsRow sx={{ mb: 3 }}>
          <DisplayStyle />
        </SettingsRow>
      </SettingsWrapper>
    </div>
  );
};

export const TextDefaultProps = {
  text: 'Text...',
  fontFamily: 'Helvetica',
  fontSize: '12',
  paddingLeft: '5',
  paddingRight: '5',
  paddingTop: '5',
  paddingBottom: '5',
  textAlign: 'left',
  fontWeight: false,
  textDecoration: false,
  fontItalic: false,
  background: 'transparent',
  fontColor: '#000000',
  size: '100',
  display: 'block',
  isDeletable: true,
};

PdfParagraph.defaultProps = TextDefaultProps;

PdfParagraph.propTypes = {
  text: PropTypes.string,
  fontSize: PropTypes.number,
  paddingLeft: PropTypes.string,
  paddingRight: PropTypes.string,
  paddingTop: PropTypes.string,
  paddingBottom: PropTypes.string,
  textAlign: PropTypes.string,
  fontWeight: PropTypes.bool,
  textDecoration: PropTypes.bool,
  fontItalic: PropTypes.bool,
  background: PropTypes.string,
  fontColor: PropTypes.string,
  display: PropTypes.string,
  size: PropTypes.string,
  display: PropTypes.string,
};

PdfParagraph.craft = {
  displayName: EDITOR_RESOLVERS.PDF.PARAGRAPH,
  name: EDITOR_RESOLVERS.PDF.PARAGRAPH,
  props: TextDefaultProps,
  related: {
    settings: PdfParagraphConfigPanel,
  },
  rules: {
    canMoveIn: () => false,
  },
};

export default PdfParagraph;
